import React, { useState } from "react";
import {
  ProfileTemplate,
  ProfileField,
} from "../profileTemplates/profileTemplate.model";

type Props = {
  template: ProfileTemplate;
  editingStepId: number | null;
  dirty: boolean;

  startEdit: (stepId: number) => void;
  cancelEdit: () => void;
  saveEdit: () => void;

  updateStepLabel: (stepId: number, label: string) => void;
  moveStep: (stepId: number, direction: "up" | "down") => void;
  deleteStep: (stepId: number) => void;

  reorderField: (
    stepId: number,
    fromKey: string,
    toKey: string,
    after: boolean,
  ) => void;

  updateField: (
    stepId: number,
    fieldKey: string,
    updates: Partial<ProfileField>,
  ) => void;

  addField: (stepId: number) => void;
  deleteField: (stepId: number, fieldKey: string) => void;

  openFormatFields: Record<string, boolean>;
  setOpenFormatFields: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;

  openVisibilityFields: Record<string, boolean>;
  setOpenVisibilityFields: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
};

export default function TemplateEditor(props: Props) {
  console.log(
    "EDITOR STEPS ORDER:",
    props.template.steps.map((s) => s.label),
  );

  const {
    template,
    editingStepId,
    dirty,
    startEdit,
    cancelEdit,
    saveEdit,
    updateStepLabel,
    moveStep,
    deleteStep,
    reorderField,
    updateField,
    addField,
    deleteField,
    openFormatFields,
    setOpenFormatFields,
    openVisibilityFields,
    setOpenVisibilityFields,
  } = props;

  // Drag-to-reorder state for fields (replaces the up/down arrows). A field is
  // dragged by its ⋮ handle; rows are drop targets within the same step.
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dragStepId, setDragStepId] = useState<number | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [dragOverPos, setDragOverPos] = useState<"before" | "after">("before");

  // Toggling Sensitive OFF disables encryption + masked display + audit on
  // future submissions for that field. Existing encrypted history stays as-is.
  // Confirm before allowing the toggle to protect against accidental clicks
  // that would silently weaken compliance posture.
  const [sensitiveOffConfirm, setSensitiveOffConfirm] = useState<{
    stepId: number;
    fieldKey: string;
    fieldLabel: string;
  } | null>(null);

  const handleSensitiveToggle = (
    stepId: number,
    field: ProfileField,
  ) => {
    if (field.sensitive) {
      setSensitiveOffConfirm({
        stepId,
        fieldKey: field.key,
        fieldLabel: field.label || field.key,
      });
      return;
    }
    updateField(stepId, field.key, { sensitive: true });
  };

  const confirmSensitiveOff = () => {
    if (!sensitiveOffConfirm) return;
    updateField(sensitiveOffConfirm.stepId, sensitiveOffConfirm.fieldKey, {
      sensitive: undefined,
    });
    setSensitiveOffConfirm(null);
  };

  return (
    <>
      {dirty && (
        <div style={{ marginBottom: 16 }}>
          <button className="btn-save" onClick={saveEdit}>
            Save Changes
          </button>
        </div>
      )}
      <div className="template-list">
        {template.steps.map((step) => (
          <div key={step.id} className="template-card">
            <div className="template-card-header">
              {editingStepId === step.id ? (
                <input
                  className="manage-input"
                  value={step.label}
                  onChange={(e) => updateStepLabel(step.id, e.target.value)}
                />
              ) : (
                <div className="template-card-title">
                  {step.label}
                  {step.scope === "internal" && (
                    <span style={{ marginLeft: 8, color: "#f4c430" }}>
                      (Internal)
                    </span>
                  )}
                </div>
              )}

              {editingStepId === step.id ? (
                <div className="step-actions">
                  <button className="btn-cancel" onClick={cancelEdit}>
                    Back
                  </button>
                  {dirty && (
                    <button className="btn-save" onClick={saveEdit}>
                      Save
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <button
                    className="btn-move"
                    onClick={() => moveStep(step.id, "up")}
                  >
                    ↑
                  </button>
                  <button
                    className="btn-move"
                    onClick={() => moveStep(step.id, "down")}
                  >
                    ↓
                  </button>
                  <button
                    className="edit-icon"
                    onClick={() => startEdit(step.id)}
                  >
                    ✏️
                  </button>
                  <button
                    className="edit-icon"
                    onClick={() => deleteStep(step.id)}
                  >
                    🗑️
                  </button>
                </div>
              )}
            </div>
            {/* Preview */}
            {editingStepId !== step.id &&
              step.fields.map((field) => (
                <div key={field.key} style={{ color: "#9aa0a6" }}>
                  {field.label}
                  {field.required ? " *" : ""}
                </div>
              ))}

            {editingStepId === step.id && (
              <div>
                <div className="template-grid-header">
                  <div className="td-handle-col" />
                  <div>Field</div>
                  <div>Type</div>
                  <div>Options</div>
                  <div>Actions</div>
                </div>

                {step.fields.map((field) => (
                  <React.Fragment key={field.key}>
                    <div
                      className={`template-field-row${
                        dragOverKey === field.key && dragStepId === step.id
                          ? dragOverPos === "after"
                            ? " td-drag-over-after"
                            : " td-drag-over-before"
                          : ""
                      }`}
                      onDragOver={(e) => {
                        if (
                          dragKey &&
                          dragStepId === step.id &&
                          dragKey !== field.key
                        ) {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                          const rect = e.currentTarget.getBoundingClientRect();
                          const pos =
                            e.clientY > rect.top + rect.height / 2
                              ? "after"
                              : "before";
                          if (dragOverKey !== field.key) setDragOverKey(field.key);
                          if (dragOverPos !== pos) setDragOverPos(pos);
                        }
                      }}
                      onDrop={(e) => {
                        if (
                          dragKey &&
                          dragStepId === step.id &&
                          dragKey !== field.key
                        ) {
                          e.preventDefault();
                          reorderField(
                            step.id,
                            dragKey,
                            field.key,
                            dragOverPos === "after",
                          );
                        }
                        setDragKey(null);
                        setDragStepId(null);
                        setDragOverKey(null);
                      }}
                      onDragLeave={() => {
                        if (dragOverKey === field.key) setDragOverKey(null);
                      }}
                    >
                      {/* Drag handle — grab to reorder this field */}
                      <span
                        className="td-drag-handle"
                        draggable
                        title="Drag to reorder"
                        onDragStart={(e) => {
                          setDragKey(field.key);
                          setDragStepId(step.id);
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", field.key);
                        }}
                        onDragEnd={() => {
                          setDragKey(null);
                          setDragStepId(null);
                          setDragOverKey(null);
                        }}
                      >
                        ⋮
                      </span>

                      <input
                        className="manage-input"
                        value={field.label}
                        onChange={(e) =>
                          updateField(step.id, field.key, {
                            label: e.target.value,
                          })
                        }
                      />

                      <select
                        className="manage-input"
                        value={field.type}
                        onChange={(e) =>
                          updateField(step.id, field.key, {
                            type: e.target.value as any,
                          })
                        }
                      >
                        <option value="text">Text</option>
                        <option value="number">Numbers only</option>
                        <option value="date">Date</option>
                        <option value="select">Dropdown</option>
                        <option value="checkbox">Checkbox</option>
                        <option value="heading">Heading</option>
                        <option value="certification">Certification</option>
                        <option value="registration">Registration</option>
                      </select>

                      {field.type === "select" ? (
                        <input
                          className="manage-input"
                          style={{ gridColumn: "4 / 5", width: "100%" }}
                          placeholder="Comma separated options"
                          value={field.options?.join(",") || ""}
                          onChange={(e) =>
                            updateField(step.id, field.key, {
                              options: e.target.value
                                .split(",")
                                .map((o) => o.trim()),
                            })
                          }
                        />
                      ) : field.type === "heading" ||
                        field.type === "certification" ||
                        field.type === "registration" ? (
                        <div />
                      ) : (
                        <div />
                      )}

                      <div className="template-field-actions">
                        <button
                          className={`btn-toggle ${
                            field.required
                              ? "btn-toggle-mandatory-on"
                              : "btn-toggle-off"
                          }`}
                          title="Mandatory"
                          onClick={() =>
                            updateField(step.id, field.key, {
                              required: !field.required,
                            })
                          }
                        >
                          M
                        </button>

                        {/* Sensitive toggle — when on, the field's submitted
                            value is encrypted at-rest and reveal requires
                            the profiles.sensitive permission. Inline style
                            on the active state so we don't need a new CSS
                            class; matches the existing btn-toggle sizing
                            via the shared base class. */}
                        <button
                          className={`btn-toggle ${
                            field.sensitive
                              ? "btn-toggle-mandatory-on"
                              : "btn-toggle-off"
                          }`}
                          style={
                            field.sensitive
                              ? { background: "#d4a017", borderColor: "#d4a017" }
                              : undefined
                          }
                          title={
                            field.sensitive
                              ? "Sensitive — encrypted at-rest, reveal requires profiles.sensitive permission"
                              : "Mark as Sensitive (encrypt + audit reveal)"
                          }
                          onClick={() => handleSensitiveToggle(step.id, field)}
                        >
                          🔒
                        </button>

                        <button
                          className="btn-toggle btn-toggle-off"
                          title="Prompt"
                          onClick={() =>
                            updateField(step.id, field.key, {
                              helper:
                                field.helper !== undefined
                                  ? undefined
                                  : field.label,
                            })
                          }
                        >
                          ✎
                        </button>

                        {(field.type === "text" || field.type === "number") && (
                          <button
                            className="btn-toggle btn-toggle-off"
                            title="Format"
                            onClick={() => {
                              const key = `${step.id}_${field.key}`;
                              setOpenFormatFields((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }));
                            }}
                          >
                            F
                          </button>
                        )}

                        <button
                          className="btn-toggle btn-toggle-off"
                          title="Show When"
                          onClick={() => {
                            const key = `${step.id}_${field.key}`;
                            setOpenVisibilityFields((prev) => ({
                              ...prev,
                              [key]: !prev[key],
                            }));
                          }}
                        >
                          👁
                        </button>

                        <button
                          className="btn-toggle btn-toggle-delete"
                          title="Delete"
                          onClick={() => deleteField(step.id, field.key)}
                        >
                          🗑
                        </button>
                      </div>
                    </div>

                    {field.helper !== undefined && (
                      <div className="field-prompt-row">
                        <div className="field-prompt-label">
                          Edit user prompt
                        </div>
                        <input
                          className="manage-input"
                          value={field.helper}
                          onChange={(e) =>
                            updateField(step.id, field.key, {
                              helper: e.target.value,
                            })
                          }
                        />
                      </div>
                    )}
                    {(field.type === "text" || field.type === "number") &&
                      openFormatFields[`${step.id}_${field.key}`] && (
                        <div className="field-prompt-row">
                          <input
                            className="manage-input"
                            type="number"
                            placeholder="Min Length"
                            value={field.minLength ?? ""}
                            onChange={(e) =>
                              updateField(step.id, field.key, {
                                minLength: e.target.value
                                  ? Number(e.target.value)
                                  : undefined,
                              })
                            }
                          />

                          <input
                            className="manage-input"
                            type="number"
                            placeholder="Max Length"
                            value={field.maxLength ?? ""}
                            onChange={(e) =>
                              updateField(step.id, field.key, {
                                maxLength: e.target.value
                                  ? Number(e.target.value)
                                  : undefined,
                              })
                            }
                          />

                          <input
                            className="manage-input"
                            type="text"
                            placeholder="Display Mask (e.g. *** *** ***)"
                            value={field.mask ?? ""}
                            onChange={(e) =>
                              updateField(step.id, field.key, {
                                mask: e.target.value || undefined,
                              })
                            }
                          />
                        </div>
                      )}
                    {openVisibilityFields[`${step.id}_${field.key}`] && (
                      <div className="field-prompt-row">
                        <select
                          className="manage-input"
                          value={field.visibleWhen?.[0]?.fieldKey || ""}
                          onChange={(e) =>
                            updateField(step.id, field.key, {
                              visibleWhen: [
                                {
                                  fieldKey: e.target.value,
                                  operator: "equals",
                                  value: "",
                                },
                              ],
                            })
                          }
                        >
                          <option value="">Select Field</option>
                          {template.steps
                            .flatMap((s) => s.fields)
                            .filter(
                              (f) => f.type === "select" && f.key !== field.key,
                            )
                            .map((f) => (
                              <option key={f.key} value={f.key}>
                                {f.label}
                              </option>
                            ))}
                        </select>

                        <select
                          className="manage-input"
                          value={field.visibleWhen?.[0]?.operator || "equals"}
                          onChange={(e) =>
                            updateField(step.id, field.key, {
                              visibleWhen: [
                                {
                                  fieldKey:
                                    field.visibleWhen?.[0]?.fieldKey || "",
                                  operator: e.target.value as
                                    | "equals"
                                    | "notEquals",
                                  value: field.visibleWhen?.[0]?.value || "",
                                },
                              ],
                            })
                          }
                        >
                          <option value="equals">equals</option>
                          <option value="notEquals">not equals</option>
                        </select>

                        {(() => {
                          const controllingField = template.steps
                            .flatMap((s) => s.fields)
                            .find(
                              (f) => f.key === field.visibleWhen?.[0]?.fieldKey,
                            );

                          if (controllingField?.type === "select") {
                            return (
                              <select
                                className="manage-input"
                                value={field.visibleWhen?.[0]?.value || ""}
                                onChange={(e) =>
                                  updateField(step.id, field.key, {
                                    visibleWhen: [
                                      {
                                        fieldKey:
                                          field.visibleWhen?.[0]?.fieldKey ||
                                          "",
                                        operator:
                                          field.visibleWhen?.[0]?.operator ||
                                          "equals",
                                        value: e.target.value,
                                      },
                                    ],
                                  })
                                }
                              >
                                <option value="">Select Value</option>
                                {controllingField.options?.map(
                                  (opt: string) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ),
                                )}
                              </select>
                            );
                          }

                          return (
                            <input
                              className="manage-input"
                              placeholder="Value"
                              value={field.visibleWhen?.[0]?.value || ""}
                              onChange={(e) =>
                                updateField(step.id, field.key, {
                                  visibleWhen: [
                                    {
                                      fieldKey:
                                        field.visibleWhen?.[0]?.fieldKey || "",
                                      operator:
                                        field.visibleWhen?.[0]?.operator ||
                                        "equals",
                                      value: e.target.value,
                                    },
                                  ],
                                })
                              }
                            />
                          );
                        })()}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
            <div className="template-field-row add-field-row">
              <div className="add-field-cell">
                <button
                  className="btn-add btn-add-row"
                  onClick={() => addField(step.id)}
                >
                  + Add Field
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {sensitiveOffConfirm && (
        <div
          className="smrtdocs-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget)
              setSensitiveOffConfirm(null);
          }}
        >
          <div
            className="smrtdocs-modal"
            style={{ width: "520px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>
                Disable Sensitive Protection?
              </h2>
            </div>
            <div className="smrtdocs-modal-body">
              <p
                style={{
                  fontSize: "0.9rem",
                  lineHeight: 1.5,
                  margin: "0 0 12px",
                }}
              >
                You are about to turn Sensitive OFF for{" "}
                <strong>{sensitiveOffConfirm.fieldLabel}</strong>. Future
                submissions of this field will:
              </p>
              <ul
                style={{
                  fontSize: "0.85rem",
                  lineHeight: 1.6,
                  margin: "0 0 12px 20px",
                  color: "#9aa0a6",
                }}
              >
                <li>no longer be encrypted at rest</li>
                <li>no longer display masked</li>
                <li>no longer require <code>profiles.sensitive</code> to view</li>
                <li>no longer write to the access audit log on view</li>
              </ul>
              <p
                style={{
                  fontSize: "0.85rem",
                  lineHeight: 1.5,
                  margin: "0 0 12px",
                  color: "#ef9a9a",
                }}
              >
                This may impact compliance and privacy obligations for this
                field type. Existing historical encrypted values for prior
                submissions are not changed.
              </p>
            </div>
            <div
              style={{
                padding: "12px 20px",
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                borderTop: "1px solid #555",
              }}
            >
              <button
                className="btn-cancel"
                onClick={() => setSensitiveOffConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                style={{ background: "#c62828", borderColor: "#c62828" }}
                onClick={confirmSensitiveOff}
              >
                Disable Sensitive
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
